import React, { FC, useCallback, useId, useMemo, useState } from 'react';

import {
	BUTTON_TYPES,
	EvBodyMedium,
	EvButton,
	EvFlexColumn,
	EvFlexRow,
	EvHeadingSmall,
	EvSvgIcon,
	EvTextInput,
	Plus,
	TEXT_COLOR
} from '@evinced-private/ui-common';

import {
	LABEL_MANAGEMENT_PANEL_ADD_VALUE_BUTTON_DAID,
	LABEL_MANAGEMENT_PANEL_ADD_VALUE_INPUT_DAID,
	LABEL_MANAGEMENT_PANEL_CSV_ERROR_BANNER_DAID,
	LABEL_MANAGEMENT_PANEL_CSV_ERROR_DISMISS_DAID,
	LABEL_MANAGEMENT_PANEL_CSV_ERROR_DOWNLOAD_DAID,
	LABEL_MANAGEMENT_PANEL_UPLOAD_VALUES_BUTTON_DAID,
	LABEL_MANAGEMENT_PANEL_UPLOAD_VALUES_ERROR_DAID,
	LABEL_MANAGEMENT_PANEL_UPLOAD_VALUES_INPUT_DAID
} from 'data-automation-ids/LabelManagementAutomationIds';

import { validateNewValue } from '../../LabelPanel.utils';

import { CsvUploadErrorBanner } from './csv-upload-error-banner';
import { FileUpload } from './file-upload';
import { downloadLabelValuesCsvErrorReport } from './LabelValuesCsvReport';
import { LABEL_VALUES_CSV_FILE_UPLOAD_VALIDATION } from './LabelValuesSection.constants';
import { LABEL_VALUES_LABELS } from './LabelValuesSection.labels';
import { LabelValuesSectionProps } from './LabelValuesSection.types';
import { ValuesTable } from './values-table';

export const LabelValuesSection: FC<LabelValuesSectionProps> = ({
	values,
	showDashboardVisibility = false,
	onAddValue,
	onRemoveValue,
	onToggleDashboardVisibility,
	onUploadCsvContent,
	onFormTouched,
	csvUploadError,
	csvRejectedFileValues,
	onDismissCsvError
}) => {
	const [newValue, setNewValue] = useState('');
	const [addValueError, setAddValueError] = useState<string | undefined>();
	const inputLabelId = useId();

	const handleAddValue = useCallback(() => {
		setNewValue((currentValue) => {
			const error = onAddValue(currentValue);
			setAddValueError(error);
			return error ? currentValue : '';
		});
	}, [onAddValue]);

	const handleInputChange = useCallback(
		(value: string) => {
			onFormTouched?.();
			setNewValue(value);
			if (!value.trim()) {
				setAddValueError(undefined);
				return;
			}
			setAddValueError(validateNewValue(value, values));
		},
		[onFormTouched, values]
	);

	const handleKeyDown = useCallback(
		(event: React.KeyboardEvent) => {
			if (event.key === 'Enter') {
				event.preventDefault();
				handleAddValue();
			}
		},
		[handleAddValue]
	);

	const handleDownloadCsvErrorReport = useCallback(() => {
		if (csvRejectedFileValues?.length) {
			downloadLabelValuesCsvErrorReport(csvRejectedFileValues);
		}
	}, [csvRejectedFileValues]);

	const fileUploadProps = useMemo(
		() => ({
			labels: {
				button: LABEL_VALUES_LABELS.FILE_UPLOAD.BUTTON,
				inputAria: LABEL_VALUES_LABELS.FILE_UPLOAD.INPUT_ARIA,
				invalidFileType: LABEL_VALUES_LABELS.FILE_UPLOAD.INVALID_FILE_TYPE,
				fileTooLarge: LABEL_VALUES_LABELS.FILE_UPLOAD.FILE_TOO_LARGE,
				fileReadError: LABEL_VALUES_LABELS.FILE_UPLOAD.FILE_READ_ERROR
			},
			validation: LABEL_VALUES_CSV_FILE_UPLOAD_VALIDATION,
			onFileContent: onUploadCsvContent,
			onTouched: onFormTouched,
			externalError: csvUploadError,
			onClearExternalError: onDismissCsvError,
			buttonDataAutomationId: LABEL_MANAGEMENT_PANEL_UPLOAD_VALUES_BUTTON_DAID,
			inputDataAutomationId: LABEL_MANAGEMENT_PANEL_UPLOAD_VALUES_INPUT_DAID,
			errorDataAutomationId: LABEL_MANAGEMENT_PANEL_UPLOAD_VALUES_ERROR_DAID
		}),
		[onUploadCsvContent, onFormTouched, csvUploadError, onDismissCsvError]
	);

	return (
		<EvFlexColumn gap="LARGE">
			<EvFlexColumn gap="MEDIUM">
				<EvHeadingSmall as="h4" color={TEXT_COLOR.CONTENT_2}>
					{LABEL_VALUES_LABELS.ADD_SUBSECTION_TITLE}
				</EvHeadingSmall>
				<EvBodyMedium color={TEXT_COLOR.CONTENT_3}>
					{LABEL_VALUES_LABELS.ADD_SUBSECTION_SUBTITLE}
				</EvBodyMedium>
				<EvBodyMedium as="label" id={inputLabelId} color={TEXT_COLOR.CONTENT_2}>
					{LABEL_VALUES_LABELS.ADD_VALUE_LABEL}
				</EvBodyMedium>
				<EvFlexRow gap="MEDIUM" align="START" wrap="NOWRAP">
					<EvFlexRow flex="10">
						<EvTextInput
							ariaLabelledBy={inputLabelId}
							value={newValue}
							onChange={handleInputChange}
							placeholder={LABEL_VALUES_LABELS.ADD_VALUE_PLACEHOLDER}
							dataAutomationId={LABEL_MANAGEMENT_PANEL_ADD_VALUE_INPUT_DAID}
							onKeyDown={handleKeyDown}
							error={addValueError}
						/>
					</EvFlexRow>
					<EvFlexRow flex="6">
						<EvFlexRow align="START" flex="0">
							<EvButton
								type={BUTTON_TYPES.SECONDARY}
								title={LABEL_VALUES_LABELS.ADD_VALUE_BUTTON}
								onClick={handleAddValue}
								dataAutomationId={LABEL_MANAGEMENT_PANEL_ADD_VALUE_BUTTON_DAID}
							>
								<EvSvgIcon icon={Plus} isSmall />
								{LABEL_VALUES_LABELS.ADD_VALUE_BUTTON}
							</EvButton>
						</EvFlexRow>
						<FileUpload {...fileUploadProps} />
					</EvFlexRow>
				</EvFlexRow>
				{csvRejectedFileValues && csvRejectedFileValues.length > 0 && (
					<CsvUploadErrorBanner
						onDownload={handleDownloadCsvErrorReport}
						onDismiss={onDismissCsvError}
						dataAutomationId={LABEL_MANAGEMENT_PANEL_CSV_ERROR_BANNER_DAID}
						downloadDataAutomationId={LABEL_MANAGEMENT_PANEL_CSV_ERROR_DOWNLOAD_DAID}
						dismissDataAutomationId={LABEL_MANAGEMENT_PANEL_CSV_ERROR_DISMISS_DAID}
					/>
				)}
			</EvFlexColumn>

			<EvFlexColumn gap="MEDIUM">
				<EvHeadingSmall as="h4" color={TEXT_COLOR.CONTENT_2}>
					{LABEL_VALUES_LABELS.EXISTING_SUBSECTION_TITLE}
				</EvHeadingSmall>
				<ValuesTable
					values={values}
					showDashboardVisibility={showDashboardVisibility}
					onRemoveValue={onRemoveValue}
					onToggleDashboardVisibility={onToggleDashboardVisibility}
				/>
			</EvFlexColumn>
		</EvFlexColumn>
	);
};
